import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import i18n from '../../assets/languages/configs';
import AvailableWeekdays from '../AvailableWeekdays/AvailableWeekdays';
import { PRICE_TYPE, TICKET_TYPE } from '../../utils/Constants';
import { formatPriceWithMinMax, getValueByLanguage } from '../../utils';
import useLocalizedMoney from '../../hooks/useLocalizedMoney';
import TextBreaks from '../TextBreaks/TextBreaks';

import styles from './style.module.css';
import { SelectNumber, TicketInfo, MultilingualType } from 'types';
import { ComboItemModel } from '@shared/ticket/get/types';

type BuyComboItemProps = {
    comboItem?: ComboItemModel[];
    reservationCombo?: TicketInfo[];
    ticketInfo?: TicketInfo;
    isConfirm?: boolean;
    selectNumber?: SelectNumber | null;
};

const BuyComboItem: React.FC<BuyComboItemProps> = ({
    comboItem = [],
    reservationCombo,
    ticketInfo,
    isConfirm,
    selectNumber,
}) => {
    const [isDisplayContent, setIsDisplayContent] = useState(false);
    const handleHeaderClick = () => {
        setIsDisplayContent(!isDisplayContent);
    };
    const { language } = i18n;

    const l = useCallback((text?: MultilingualType | null) => getValueByLanguage(text, language), [language]);
    const { t } = useTranslation();
    const { formatMoney } = useLocalizedMoney();

    const unitPriceList = (ticket: any) => {
        if (!ticket) {
            return [];
        }

        const result = [];
        if (ticket.isReserve) {
            const {
                priceAdultMin,
                priceAdultMax,
                priceCustom1Min,
                priceCustom1Max,
                priceCustom2Min,
                priceCustom2Max,
                priceCustom3Min,
                priceCustom3Max,
            } = ticket;

            const priceConfigs = [
                { minPrice: priceAdultMin, maxPrice: priceAdultMax, label: t('ticket-detail.adult') },
                { minPrice: priceCustom1Min, maxPrice: priceCustom1Max, label: l(ticketInfo?.custom1Name) },
                { minPrice: priceCustom2Min, maxPrice: priceCustom2Max, label: l(ticketInfo?.custom2Name) },
                { minPrice: priceCustom3Min, maxPrice: priceCustom3Max, label: l(ticketInfo?.custom3Name) },
            ];

            priceConfigs.forEach(({ minPrice, maxPrice, label }) => {
                if (label && (minPrice || maxPrice)) {
                    const price = formatPriceWithMinMax({ minPrice, maxPrice }, formatMoney);
                    result.push({ label, price });
                }
            });
        } else {
            if (ticket.referencePriceAdult) {
                result.push({
                    label: t('ticket-detail.adult'),
                    price: ticket.referencePriceAdult,
                });
            }
            if (ticket.referencePriceCustom1 && l(ticketInfo?.custom1Name)) {
                result.push({
                    label: l(ticketInfo?.custom1Name),
                    price: ticket.referencePriceCustom1,
                });
            }
            if (ticket.referencePriceCustom2 && l(ticketInfo?.custom2Name)) {
                result.push({
                    label: l(ticketInfo?.custom2Name),
                    price: ticket.referencePriceCustom2,
                });
            }
            if (ticket.referencePriceCustom3 && l(ticketInfo?.custom3Name)) {
                result.push({
                    label: l(ticketInfo?.custom1Name),
                    price: ticket.referencePriceCustom3,
                });
            }
        }

        return result;
    };

    const findReservationInfo = (comboItem: any, reservationCombo: any) => {
        return reservationCombo?.find((reservation: any) => comboItem.displayPriority === reservation?.displayPriority)
            ?.reservationInfo;
    };

    const getPrice = (item: any, reservationInfo: any) => {
        let priceAdult;
        let priceCustom1;
        let priceCustom2;
        let priceCustom3;

        if (reservationInfo && item?.isReserve) {
            priceAdult = reservationInfo.priceAdult;
            priceCustom1 = reservationInfo.priceCustom1;
            priceCustom2 = reservationInfo.priceCustom2;
            priceCustom3 = reservationInfo.priceCustom3;
        } else {
            priceAdult = item.referencePriceAdult;
            priceCustom1 = item.referencePriceCustom1;
            priceCustom2 = item.referencePriceCustom2;
            priceCustom3 = item.referencePriceCustom3;
        }
        return {
            priceAdult,
            priceCustom1,
            priceCustom2,
            priceCustom3,
        };
    };

    const calculateCouponCount = (couponCount?: number | null, selectedNumber?: number | null) => {
        return (couponCount ?? 0) * (selectedNumber ?? 0);
    };

    return (
        comboItem.length > 0 && (
            <div className={styles.comboItem}>
                <div className={styles.collapseHeader}>
                    <div className={styles.collapseHeaderTitle}>{t('ticket-detail.set-ticket-details')}</div>
                    <div className={styles.collapseHeaderIcon} onClick={handleHeaderClick}>
                        {isDisplayContent ? (
                            <span className={styles.icon}>-</span>
                        ) : (
                            <span className={styles.icon}>+</span>
                        )}
                    </div>
                </div>
                {isDisplayContent && (
                    <div className={styles.collapseContent}>
                        {comboItem.map((item, index) => {
                            const reservationInfo = findReservationInfo(item, reservationCombo);
                            return (
                                <div className={styles.collapseContentItem} key={index}>
                                    <p className={styles.ticketName}>
                                        <TextBreaks text={l(item.ticketName)} />
                                    </p>
                                    <p className={styles.subtitle}>
                                        <TextBreaks text={l(item.subtitle)} />
                                    </p>
                                    {/*使用開始可能曜日*/}
                                    {!item?.isReserve && (
                                        <AvailableWeekdays ticket={item} className={styles.textContent} />
                                    )}
                                    {/* 回数券枚数 */}
                                    {item.ticketType === TICKET_TYPE.COUPON && (
                                        <div className={styles.couponNumber}>
                                            {ticketInfo?.isInUseAdult && item?.isInUseAdult && (
                                                <span>
                                                    {t('ticket-detail.adult') +
                                                        ': ' +
                                                        t('ticket-detail.coupon-count', {
                                                            count: calculateCouponCount(
                                                                item?.couponCount,
                                                                selectNumber?.adult,
                                                            ),
                                                        })}
                                                </span>
                                            )}
                                            {ticketInfo?.isInUseCustom1 && item?.isInUseCustom1 && (
                                                <span>
                                                    {l(ticketInfo.custom1Name) +
                                                        ': ' +
                                                        t('ticket-detail.coupon-count', {
                                                            count: calculateCouponCount(
                                                                item?.couponCount,
                                                                selectNumber?.custom1,
                                                            ),
                                                        })}
                                                </span>
                                            )}
                                            {ticketInfo?.isInUseCustom2 && item?.isInUseCustom2 && (
                                                <span>
                                                    {l(ticketInfo.custom2Name) +
                                                        ': ' +
                                                        t('ticket-detail.coupon-count', {
                                                            count: calculateCouponCount(
                                                                item?.couponCount,
                                                                selectNumber?.custom2,
                                                            ),
                                                        })}
                                                </span>
                                            )}
                                            {ticketInfo?.isInUseCustom3 && item?.isInUseCustom3 && (
                                                <span>
                                                    {l(ticketInfo.custom3Name) +
                                                        ': ' +
                                                        t('ticket-detail.coupon-count', {
                                                            count: calculateCouponCount(
                                                                item?.couponCount,
                                                                selectNumber?.custom3,
                                                            ),
                                                        })}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {isConfirm && (
                                        <>
                                            {reservationInfo && item.isReserve && (
                                                <>
                                                    <p className={styles.subtitle}>
                                                        <TextBreaks text={l(reservationInfo?.reservationClassName)} />
                                                    </p>
                                                    <p className={styles.subtitle}>
                                                        <TextBreaks
                                                            text={l(reservationInfo?.stockPartitionDescription)}
                                                        />
                                                    </p>
                                                    <p className={styles.subtitle}>
                                                        <TextBreaks
                                                            text={l(reservationInfo?.reservationPriceDescription)}
                                                        />
                                                    </p>
                                                </>
                                            )}
                                            {ticketInfo?.priceType === PRICE_TYPE.ADDITION && (
                                                <div className={styles.totalPrice}>
                                                    {(selectNumber?.adult ?? 0) > 0 && (
                                                        <div className={styles.price}>
                                                            <span>{t('ticket-detail.adult')}</span> /{' '}
                                                            <span>
                                                                {formatMoney(
                                                                    getPrice(item, reservationInfo).priceAdult,
                                                                    'JPY',
                                                                )}
                                                            </span>{' '}
                                                            x <span>{selectNumber?.adult}</span>
                                                        </div>
                                                    )}
                                                    {(selectNumber?.custom1 ?? 0) > 0 && (
                                                        <div className={styles.price}>
                                                            <span>{l(ticketInfo.custom1Name)}</span> /{' '}
                                                            <span>
                                                                {formatMoney(
                                                                    getPrice(item, reservationInfo).priceCustom1,
                                                                    'JPY',
                                                                )}
                                                            </span>{' '}
                                                            x <span>{selectNumber?.custom1}</span>
                                                        </div>
                                                    )}
                                                    {(selectNumber?.custom2 ?? 0) > 0 && (
                                                        <div className={styles.price}>
                                                            <span>{l(ticketInfo.custom2Name)}</span> /{' '}
                                                            <span>
                                                                {formatMoney(
                                                                    getPrice(item, reservationInfo).priceCustom2,
                                                                    'JPY',
                                                                )}
                                                            </span>{' '}
                                                            x <span>{selectNumber?.custom2}</span>
                                                        </div>
                                                    )}
                                                    {(selectNumber?.custom3 ?? 0) > 0 && (
                                                        <div className={styles.price}>
                                                            <span>{l(ticketInfo.custom3Name)}</span> /{' '}
                                                            <span>
                                                                {formatMoney(
                                                                    getPrice(item, reservationInfo).priceCustom3,
                                                                    'JPY',
                                                                )}
                                                            </span>{' '}
                                                            x <span>{selectNumber?.custom3}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {ticketInfo?.isReserve &&
                                        !isConfirm &&
                                        ticketInfo?.priceType === PRICE_TYPE.ADDITION && (
                                            <div className={styles.ticketPrice}>
                                                {unitPriceList(item).map((unitPrice, index) => (
                                                    <span key={index} style={{ marginRight: 10 }}>
                                                        {unitPrice.label} /{' '}
                                                        {item.isReserve
                                                            ? unitPrice.price
                                                            : formatMoney(unitPrice.price, 'JPY')}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        )
    );
};

export default BuyComboItem;
